import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button } from '@chakra-ui/react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useMutation, useQuery } from 'urql';
import { useParams } from 'react-router-dom';
import {
  LineSegment2,
  LinearRing2,
  Point2,
} from '../../../domain/geometry/geometric-types';
import { useSheetViewer } from '../../common/SheetViewer';
import { getUnitScaleFromCalibration } from '../../../domain/sheet-calibration';
import { toJstsMultiPolygon } from '../../../domain/geometry/algorithms/util/polygon';
import { createMultipolygon } from '../shapes/util';
import {
  BulkUpsertSparkelPropertiesDocument,
  CreateOrderEntryFromTemplateDocument,
  CreateOrderEntryFromTemplateInput,
  CreateSheetShapeDocument,
  CreateSheetShapeInput,
  ForgeAttributeQueryTypeInput,
  GetOrderEntryTemplatesPageDocument,
} from '../../../gql/graphql';
import { getSheetShapeUrn } from '../../../services/viewer/SheetShapesManager';
import { updateWedgeProps } from '../../../hooks/protan-special-handling';
import { useResolvedOrder } from '../../../hooks/order';
import { calculateSegmentLength } from '../../../domain/geometry/algorithms/util/line-segment';
import { DraggableSheetEdge } from './SheetEdge';
import { DraggableVertex } from './SheetVertex';
import { DiamondLabel } from './DiamondLabel';
import {
  addWedgesOnEachSide,
  calculateNormals,
  calculatePolygonCenter,
  findLongestAdjacentSegment,
  findSecondLongestParallelSegment,
  isClockwise,
  placePointsOnLines,
  updateDiamondProperties as determineWedgesForLine,
  linearRing2ToLineSegments,
} from './auto-generated-roofs';
import { Diamond, KileTypeSKU } from './SheetWedgeShapeDrawing';
import { kileSelector } from './kile-selector';

interface PolygonVisualizerProps {
  vertices: LinearRing2;
  getPointInDomCoordinateSystem: (pointInPdf: Point2) => Point2;
}

const PolygonVisualizer: React.FC<PolygonVisualizerProps> = ({
  vertices,
  getPointInDomCoordinateSystem,
}) => {
  const [
    automaticDrainAndWedgesPlacement,
    setAutomaticDrainAndWedgesPlacement,
  ] = useState<boolean>(false);

  const { projectId, orderId } = useParams<{
    projectId: string;
    orderId: string;
  }>() as {
    projectId: string;
    orderId: string;
  };

  const { pageNumber, activeSheetId } = useSheetViewer();

  const [createShapeResult, createShapeMutation] = useMutation(
    CreateSheetShapeDocument
  );

  const [{ data: resourceTemplates }] = useQuery({
    query: GetOrderEntryTemplatesPageDocument,
  });

  const order = useResolvedOrder(projectId, orderId);

  const handleSubmitDrains = async (
    points: Point2[],
    projectId: string,
    sheetId: string,
    sheetPageNumber: number
  ) => {
    const pointSheetShapeInput: CreateSheetShapeInput[] = points.map(
      (point) => {
        return {
          sheetShape: {
            projectId,
            sheetId,
            urn: getSheetShapeUrn(projectId),
            sheetPageNumber,
            name: 'Drain',
            sheetShapePoint: {
              create: [
                {
                  point: {
                    x: point[0],
                    y: point[1],
                  },
                },
              ],
            },
          },
        };
      }
    );
    pointSheetShapeInput.forEach((shapeInput) => {
      createShapeMutation({ input: shapeInput });
    });
  };

  const [, bulkCreateSparkelProps] = useMutation(
    BulkUpsertSparkelPropertiesDocument
  );
  const [, createOrderEntryFromTemplate] = useMutation(
    CreateOrderEntryFromTemplateDocument
  );

  const handleCreateFromTemplateClick = useCallback(
    async (
      templateId: string,
      title: string,
      query: ForgeAttributeQueryTypeInput | undefined,
      orderId: string
    ) => {
      const input: CreateOrderEntryFromTemplateInput = {
        orderEntryTemplateId: templateId,
        orderId,
        title,
        color: '#FFFFFF',
        query,
        dbids: null,
      };
      await createOrderEntryFromTemplate({ input });
    },
    [createOrderEntryFromTemplate]
  );
  const handleSubmitWedges = async (
    lineSegments: LineSegment2[],
    projectId: string,
    sheetId: string,
    sheetPageNumber: number,
    diamonds: Diamond[],
    kilteTypeSku: KileTypeSKU[]
  ) => {
    const lineInputs = lineSegments.map((lineSegment) => {
      return {
        sheetShape: {
          projectId,
          sheetId,
          urn: getSheetShapeUrn(projectId),
          sheetPageNumber,
          name: 'Wedge',
          sheetShapeLine: {
            create: [
              {
                points: [
                  { x: lineSegment[0][0], y: lineSegment[0][1] },
                  { x: lineSegment[1][0], y: lineSegment[1][1] },
                ],
              },
            ],
          },
        },
      };
    });
    lineInputs.forEach(async (shapeInput, index) => {
      const result = await createShapeMutation({ input: shapeInput });
      if (result.data)
        updateWedgeProps(
          result?.data,
          diamonds[index],
          kilteTypeSku[index],
          bulkCreateSparkelProps,
          projectId,
          orderId,
          handleCreateFromTemplateClick,
          resourceTemplates,
          order
        );
    });
  };

  useHotkeys('tab', (e) => {
    e.preventDefault();
    e.stopPropagation();
    // For each tab press, cycle through the possible diamonds, and set the current indexed diamond to the next one
    setAutomaticDrainAndWedgesPlacement(!automaticDrainAndWedgesPlacement);
  });

  const edgeDistanceFactor = 0.5;

  const {
    calibration: { calibration },
  } = useSheetViewer();
  const unitScale = useMemo(
    () =>
      calibration ? getUnitScaleFromCalibration(calibration.calibration) : 0,
    [calibration]
  );

  const polygon = toJstsMultiPolygon(createMultipolygon([vertices]));

  const size = polygon.getArea() * unitScale * unitScale;
  //Rule of thumb from Svein Arne, drain per 300 or 200.
  const antallSluk = Math.max(size / 300, 2);

  const normalizedVertices = vertices;

  // Find the longest and second-longest segments
  const { longestSegment } = findLongestAdjacentSegment(normalizedVertices);
  const { secondLongestSegment } = findSecondLongestParallelSegment(
    normalizedVertices,
    longestSegment
  );

  // Place points on the lines
  const { pointsOnLine1, pointsOnLine2 } = placePointsOnLines(
    antallSluk,
    longestSegment,
    secondLongestSegment,
    edgeDistanceFactor,
    unitScale
  );

  const clockwise = isClockwise(normalizedVertices);
  const normalsLine1 = calculateNormals(pointsOnLine1, clockwise);
  const normalsLine2 = calculateNormals(pointsOnLine2, clockwise);

  const segmentsLine1 = linearRing2ToLineSegments(pointsOnLine1);
  const segmentsLine2 = linearRing2ToLineSegments(pointsOnLine2);

  const segments1: LineSegment2[] = [[longestSegment[0], pointsOnLine1[0]]];
  const endSegment: LineSegment2 = [
    pointsOnLine1[pointsOnLine1.length - 1],
    longestSegment[longestSegment.length - 1],
  ];

  segments1.push(...segmentsLine1, endSegment);

  const segments2: LineSegment2[] = [
    [secondLongestSegment[0], pointsOnLine2[0]],
  ];
  const endSegment2: LineSegment2 = [
    pointsOnLine2[pointsOnLine2.length - 1],
    secondLongestSegment[secondLongestSegment.length - 1],
  ];

  segments2.push(...segmentsLine2, endSegment2);

  const center = calculatePolygonCenter(normalizedVertices);

  //Kan ta det basert på start og slutt? Ja, ble alltid riktig
  let diamondsLine1 = determineWedgesForLine(
    normalsLine1,
    pointsOnLine1,
    clockwise,
    center
  );

  let diamondsLine2 = determineWedgesForLine(
    normalsLine2,
    pointsOnLine2,
    clockwise,
    center
  );

  diamondsLine1 = addWedgesOnEachSide(diamondsLine1);
  diamondsLine2 = addWedgesOnEachSide(diamondsLine2);

  const kilerLine1 = segments1.map(
    (segment, index) =>
      (kileSelector(
        diamondsLine1[index],
        calculateSegmentLength(segment) * unitScale
      ) as KileTypeSKU) ?? KileTypeSKU.INVALID_KILE
  );
  const kilerLine2 = segments2.map(
    (segment, index) =>
      (kileSelector(
        diamondsLine2[index],
        calculateSegmentLength(segment) * unitScale
      ) as KileTypeSKU) ?? KileTypeSKU.INVALID_KILE
  );

  return (
    <>
      {automaticDrainAndWedgesPlacement && (
        <>
          {segments1.map((segment, index) => (
            <DiamondLabel
              key={index}
              segment={segment}
              getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
              diamond={diamondsLine1[index]}
            ></DiamondLabel>
          ))}
          {segments2.map((segment, index) => (
            <DiamondLabel
              key={index}
              segment={segment}
              getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
              diamond={diamondsLine2[index]}
            ></DiamondLabel>
          ))}

          {normalizedVertices.slice(0, -1).map((point, index) => (
            <DraggableSheetEdge
              key={index}
              onDoubleClick={() => {
                console.log('hey');
              }}
              onEdgeDrag={() => {
                console.log('hey');
              }}
              segment={[
                [point[0], point[1]],
                [
                  normalizedVertices[index + 1][0],
                  normalizedVertices[index + 1][1],
                ],
              ]}
              backgroundColor={'#FFFFFF'}
              getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
            />
          ))}
          {pointsOnLine1.map((point, index) => (
            <DraggableVertex
              key={index}
              theme={'default'}
              point={point}
              onDoubleClick={() => {
                console.log('Hey');
              }}
              onVertexDrag={() => {
                console.log('Hey');
              }}
              getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
            />
          ))}

          {pointsOnLine2.map((point, index) => (
            <DraggableVertex
              key={index}
              theme={'default'}
              point={point}
              onDoubleClick={() => {
                console.log('Hey');
              }}
              onVertexDrag={() => {
                console.log('Hey');
              }}
              getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
            />
          ))}
        </>
      )}

      <Button
        onClick={() => {
          if (activeSheetId && kilerLine1 && kilerLine2) {
            handleSubmitDrains(
              pointsOnLine1,
              projectId,
              activeSheetId,
              pageNumber
            );
            handleSubmitDrains(
              pointsOnLine2,
              projectId,
              activeSheetId,
              pageNumber
            );
            handleSubmitWedges(
              segments1,
              projectId,
              activeSheetId,
              pageNumber,
              diamondsLine1,
              kilerLine1
            );
            handleSubmitWedges(
              segments2,
              projectId,
              activeSheetId,
              pageNumber,
              diamondsLine2,
              kilerLine2
            );
          }
        }}
      >
        Submit
      </Button>
    </>
  );
};

// Example usage of the component
const AutoGeneratedRoof: React.FC<PolygonVisualizerProps> = ({
  vertices,
  getPointInDomCoordinateSystem,
}) => {
  return (
    <Box p={4}>
      <PolygonVisualizer
        getPointInDomCoordinateSystem={getPointInDomCoordinateSystem}
        vertices={vertices}
      />
    </Box>
  );
};

export default AutoGeneratedRoof;
